import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Shield, Upload, Trash2, CheckCircle, AlertCircle, FileKey, Settings, Eye, EyeOff, Plus, User, Edit2 } from 'lucide-react';
import api from '../../lib/api';

export default function AdminCertificados() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Lista de assinantes
  const [assinantes, setAssinantes] = useState([]);
  const [editingAssinante, setEditingAssinante] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Form do assinante
  const [assinanteForm, setAssinanteForm] = useState({
    id: null,
    nome: '',
    cargo: '',
    cpf: '',
    email: '',
    tipo_certificado: 'simples',
    certificado_arquivo: null,
    certificado_nome: null,
    ativo: true
  });
  
  // Configuração de assinatura
  const [assinaturaConfig, setAssinaturaConfig] = useState({
    tipo_padrao: 'simples',
    exibir_hash: true,
    exibir_qrcode: true,
    texto_rodape: 'Documento assinado digitalmente conforme MP 2.200-2/2001',
    assinante_padrao: null
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [certPassword, setCertPassword] = useState('');

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const [certRes, assRes] = await Promise.all([
        api.get('/config/certificados'),
        api.get('/config/assinatura')
      ]);
      
      // Converter formato antigo para novo (array de assinantes)
      if (certRes.data) {
        if (Array.isArray(certRes.data.assinantes)) {
          setAssinantes(certRes.data.assinantes);
        } else if (certRes.data.nome_assinante) {
          // Formato antigo - converter para array
          setAssinantes([{
            id: '1',
            nome: certRes.data.nome_assinante,
            cargo: certRes.data.cargo_assinante || '',
            cpf: certRes.data.cpf_assinante || '',
            email: '',
            tipo_certificado: certRes.data.tipo || 'simples',
            certificado_arquivo: certRes.data.certificado_arquivo,
            certificado_nome: certRes.data.certificado_nome,
            ativo: true
          }]);
        }
      }
      
      if (assRes.data) setAssinaturaConfig(assRes.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAssinantes = async (updatedAssinantes) => {
    try {
      setSaving(true);
      await api.post('/config/certificados', { assinantes: updatedAssinantes });
      setAssinantes(updatedAssinantes);
      toast.success('Assinantes salvos com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar assinantes');
    } finally {
      setSaving(false);
    }
  };

  const saveAssinaturaConfig = async () => {
    try {
      setSaving(true);
      await api.post('/config/assinatura', assinaturaConfig);
      toast.success('Configuração de assinatura salva!');
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAssinante = () => {
    setAssinanteForm({
      id: null,
      nome: '',
      cargo: '',
      cpf: '',
      email: '',
      tipo_certificado: 'simples',
      certificado_arquivo: null,
      certificado_nome: null,
      ativo: true
    });
    setEditingAssinante(null);
    setShowAddForm(true);
  };

  const handleEditAssinante = (assinante) => {
    setAssinanteForm({ ...assinante });
    setEditingAssinante(assinante.id);
    setShowAddForm(true);
  };

  const handleSaveAssinante = async () => {
    if (!assinanteForm.nome || !assinanteForm.cargo) {
      toast.error('Nome e Cargo são obrigatórios');
      return;
    }

    let updatedAssinantes;
    if (editingAssinante) {
      // Editando existente
      updatedAssinantes = assinantes.map(a => 
        a.id === editingAssinante ? { ...assinanteForm } : a
      );
    } else {
      // Adicionando novo
      const newAssinante = {
        ...assinanteForm,
        id: Date.now().toString()
      };
      updatedAssinantes = [...assinantes, newAssinante];
    }

    await saveAssinantes(updatedAssinantes);
    setShowAddForm(false);
    setEditingAssinante(null);
  };

  const handleDeleteAssinante = async (id) => {
    if (!confirm('Tem certeza que deseja remover este assinante?')) return;
    
    const updatedAssinantes = assinantes.filter(a => a.id !== id);
    await saveAssinantes(updatedAssinantes);
  };

  const handleCertUpload = async (e, assinanteId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pfx') && !file.name.toLowerCase().endsWith('.p12')) {
      toast.error('Formato inválido. Use arquivos .pfx ou .p12');
      return;
    }
    
    if (!certPassword) {
      toast.error('Informe a senha do certificado antes de fazer upload');
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('senha', certPassword);
      formData.append('assinante_id', assinanteId);
      
      const res = await api.post('/config/certificados/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Certificado ${res.data.filename} enviado com sucesso!`);
      fetchConfigs();
      setCertPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar certificado');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Certificados e Assinatura Digital</h1>
          <p className="text-gray-600">Gerencie assinantes e certificados digitais ICP-Brasil</p>
        </div>

        <Tabs defaultValue="assinantes" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="assinantes" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Assinantes
            </TabsTrigger>
            <TabsTrigger value="configuracoes" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configurações
            </TabsTrigger>
          </TabsList>

          {/* ABA ASSINANTES */}
          <TabsContent value="assinantes" className="space-y-6">
            {/* Botão Adicionar */}
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Lista de Assinantes</h2>
              <Button onClick={handleAddAssinante} className="bg-red-700 hover:bg-red-800">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Assinante
              </Button>
            </div>

            {/* Formulário de Adicionar/Editar */}
            {showAddForm && (
              <Card className="border-2 border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-red-700" />
                    {editingAssinante ? 'Editar Assinante' : 'Novo Assinante'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome Completo *</Label>
                      <Input
                        value={assinanteForm.nome}
                        onChange={(e) => setAssinanteForm({...assinanteForm, nome: e.target.value})}
                        placeholder="Nome completo do assinante"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cargo/Função *</Label>
                      <Input
                        value={assinanteForm.cargo}
                        onChange={(e) => setAssinanteForm({...assinanteForm, cargo: e.target.value})}
                        placeholder="Ex: Prefeito Municipal"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>CPF</Label>
                      <Input
                        value={assinanteForm.cpf}
                        onChange={(e) => setAssinanteForm({...assinanteForm, cpf: e.target.value})}
                        placeholder="000.000.000-00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>E-mail</Label>
                      <Input
                        type="email"
                        value={assinanteForm.email}
                        onChange={(e) => setAssinanteForm({...assinanteForm, email: e.target.value})}
                        placeholder="email@exemplo.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo de Certificado</Label>
                    <Select 
                      value={assinanteForm.tipo_certificado} 
                      onValueChange={(value) => setAssinanteForm({...assinanteForm, tipo_certificado: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="simples">Simples (Hash SHA-256)</SelectItem>
                        <SelectItem value="avancada">Avançada (com dados do assinante)</SelectItem>
                        <SelectItem value="icp_brasil">ICP-Brasil (com certificado digital)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Upload de Certificado se tipo for ICP-Brasil */}
                  {assinanteForm.tipo_certificado === 'icp_brasil' && editingAssinante && (
                    <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                      <h4 className="font-medium">Certificado Digital A1</h4>
                      
                      {assinanteForm.certificado_nome ? (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          Certificado: {assinanteForm.certificado_nome}
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label>Senha do Certificado</Label>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Senha do certificado"
                                value={certPassword}
                                onChange={(e) => setCertPassword(e.target.value)}
                              />
                              <button
                                type="button"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Arquivo (.pfx ou .p12)</Label>
                            <Input
                              type="file"
                              accept=".pfx,.p12"
                              onChange={(e) => handleCertUpload(e, editingAssinante)}
                              disabled={uploading || !certPassword}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={assinanteForm.ativo}
                      onCheckedChange={(checked) => setAssinanteForm({...assinanteForm, ativo: checked})}
                    />
                    <Label>Assinante Ativo</Label>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleSaveAssinante} disabled={saving} className="bg-red-700 hover:bg-red-800">
                      {saving ? 'Salvando...' : 'Salvar Assinante'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddForm(false)}>
                      Cancelar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lista de Assinantes */}
            {assinantes.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-10">
                  <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">Nenhum assinante cadastrado</p>
                  <p className="text-sm text-gray-500">Clique em "Adicionar Assinante" para começar</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {assinantes.map((assinante) => (
                  <Card key={assinante.id} className={!assinante.ativo ? 'opacity-60' : ''}>
                    <CardContent className="flex items-center justify-between py-4">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${assinante.tipo_certificado === 'icp_brasil' ? 'bg-green-100' : 'bg-blue-100'}`}>
                          {assinante.tipo_certificado === 'icp_brasil' ? (
                            <Shield className="w-6 h-6 text-green-600" />
                          ) : (
                            <User className="w-6 h-6 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{assinante.nome}</h3>
                          <p className="text-sm text-gray-600">{assinante.cargo}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              assinante.tipo_certificado === 'icp_brasil' 
                                ? 'bg-green-100 text-green-700' 
                                : assinante.tipo_certificado === 'avancada'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {assinante.tipo_certificado === 'icp_brasil' ? 'ICP-Brasil' : 
                               assinante.tipo_certificado === 'avancada' ? 'Avançada' : 'Simples'}
                            </span>
                            {assinante.certificado_nome && (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Certificado OK
                              </span>
                            )}
                            {!assinante.ativo && (
                              <span className="text-xs text-red-600">Inativo</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditAssinante(assinante)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteAssinante(assinante.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ABA CONFIGURAÇÕES */}
          <TabsContent value="configuracoes" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Configurações de Exibição</CardTitle>
                <CardDescription>
                  Configure como a assinatura digital será exibida nos documentos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Exibir Hash do Documento</Label>
                    <p className="text-sm text-gray-500">Mostra o hash SHA-256 no PDF</p>
                  </div>
                  <Switch
                    checked={assinaturaConfig.exibir_hash}
                    onCheckedChange={(checked) => setAssinaturaConfig({...assinaturaConfig, exibir_hash: checked})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Exibir QR Code de Verificação</Label>
                    <p className="text-sm text-gray-500">Adiciona QR Code para verificação online</p>
                  </div>
                  <Switch
                    checked={assinaturaConfig.exibir_qrcode}
                    onCheckedChange={(checked) => setAssinaturaConfig({...assinaturaConfig, exibir_qrcode: checked})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Texto de Rodapé da Assinatura</Label>
                  <Input
                    value={assinaturaConfig.texto_rodape || ''}
                    onChange={(e) => setAssinaturaConfig({...assinaturaConfig, texto_rodape: e.target.value})}
                    placeholder="Ex: Documento assinado digitalmente conforme MP 2.200-2/2001"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Assinante Padrão</Label>
                  <Select 
                    value={assinaturaConfig.assinante_padrao || 'none'} 
                    onValueChange={(value) => setAssinaturaConfig({...assinaturaConfig, assinante_padrao: value === 'none' ? null : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o assinante padrão" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum (selecionar na publicação)</SelectItem>
                      {assinantes.filter(a => a.ativo).map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.nome} - {a.cargo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Tipo de Assinatura Padrão</Label>
                  <Select 
                    value={assinaturaConfig.tipo_padrao} 
                    onValueChange={(value) => setAssinaturaConfig({...assinaturaConfig, tipo_padrao: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simples">Simples</SelectItem>
                      <SelectItem value="avancada">Avançada</SelectItem>
                      <SelectItem value="icp_brasil">ICP-Brasil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={saveAssinaturaConfig} disabled={saving} className="bg-red-700 hover:bg-red-800">
                  {saving ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
              </CardContent>
            </Card>

            {/* Preview da Assinatura */}
            <Card>
              <CardHeader>
                <CardTitle>Preview da Assinatura</CardTitle>
                <CardDescription>
                  Visualização de como a assinatura aparecerá nos documentos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                  <div className="flex items-center gap-2 text-green-700 font-medium">
                    <CheckCircle className="w-5 h-5" />
                    DOCUMENTO ASSINADO DIGITALMENTE
                  </div>
                  <div className="text-sm text-green-600 space-y-1">
                    {assinantes.length > 0 ? (
                      <>
                        <p><strong>Assinante:</strong> {assinantes[0].nome}</p>
                        <p><strong>Cargo:</strong> {assinantes[0].cargo}</p>
                      </>
                    ) : (
                      <p><strong>Assinante:</strong> (nenhum cadastrado)</p>
                    )}
                    <p><strong>Data:</strong> {new Date().toLocaleDateString('pt-BR')}</p>
                    {assinaturaConfig.exibir_hash && (
                      <p><strong>Hash:</strong> A1B2C3D4E5F6...</p>
                    )}
                  </div>
                  {assinaturaConfig.texto_rodape && (
                    <p className="text-xs text-green-500 mt-2 pt-2 border-t border-green-200">
                      {assinaturaConfig.texto_rodape}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
